import { atom } from 'jotai';
import { AuthUser } from '../types';

/**
 * Helper function to clean username by removing AmazonFederate_ prefix
 */
export const cleanUsername = (username: string): string => {
  if (username.startsWith('AmazonFederate_')) {
    return username.substring(15);
  }
  return username;
};

/**
 * Atom to store the authenticated user
 */
const userAtom = atom<AuthUser | null>(null);

/**
 * Read-only atom to get the authenticated user with cleaned username
 */
export const getUserAtom = atom<AuthUser | null>((get) => {
  const user = get(userAtom);
  if (!user) return null;
  
  // Return user with cleaned username
  return {
    ...user,
    username: cleanUsername(user.username)
  };
});

/**
 * Write-only atom to set the authenticated user
 */
export const setUserAtom = atom(
  null,
  (_get, set, update: AuthUser | null) => {
    set(userAtom, update);
  }
);

/**
 * Atom to check if user is authenticated
 */
export const isAuthenticatedAtom = atom<boolean>((get) => get(userAtom) !== null);

/**
 * Atom to get clean username (without AmazonFederate_ prefix)
 */
export const cleanUsernameAtom = atom<string | null>((get) => {
  const user = get(getUserAtom);
  return user?.username || null;
});

/**
 * Atom to get user display name
 */
export const userDisplayNameAtom = atom<string>((get) => {
  const user = get(getUserAtom); // Use getUserAtom which already has cleaned username
  if (!user) return 'Guest';
  
  // Try to get display name from attributes
  if (user.attributes?.name) {
    return user.attributes.name;
  }
  
  // Fall back to cleaned username
  return user.username;
});

/**
 * Atom to get user email
 */
export const userEmailAtom = atom<string | null>((get) => {
  const user = get(userAtom);
  return user?.attributes?.email || null;
});

/**
 * Atom to get user initials for avatar
 */
export const userInitialsAtom = atom<string>((get) => {
  const displayName = get(userDisplayNameAtom);
  
  if (displayName === 'Guest') {
    return 'G';
  }
  
  const parts = displayName.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  
  return displayName.substring(0, 2).toUpperCase();
});
